import { List as ImList, Record as ImRecord, type RecordOf } from 'immutable';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';
import { transactionsTypes } from 'app-components/flux/transactions';

export const TAXES_REDUCER_KEY = 'taxesStore';
export const TAXES_VERSION = 1;

export const filingStatuses = [
  'SINGLE',
  'SURVIVING_SPOUSE',
  'HEAD_OF_HOUSEHOLD',
  'MARRIED_JOINT',
  'MARRIED_SEPARATE',
] as const;
export type FilingStatus = (typeof filingStatuses)[number];

export type WagesProps = {
  selfWages?: number;
  selfWagesOther?: number;
  spouseWages?: number;
  spouseWagesOther?: number;
};
export type Wages = RecordOf<WagesProps>;
export const mkWages = ImRecord<WagesProps>({
  selfWages: undefined,
  selfWagesOther: undefined,
  spouseWages: undefined,
  spouseWagesOther: undefined,
});

export type TaxPaymentsProps = {
  estimatedTaxesPaidOrPlannedApril?: number;
  estimatedTaxesPaidOrPlannedJune?: number;
  estimatedTaxesPaidOrPlannedSeptember?: number;
  estimatedTaxesPaidOrPlannedJanuary?: number;
  refundAppliedFromPriorYearFederalReturn?: number;
  estTaxAtLowerPercent?: boolean;
};
export type TaxPayments = RecordOf<TaxPaymentsProps>;
export const mkTaxPayments = ImRecord<TaxPaymentsProps>({
  estimatedTaxesPaidOrPlannedApril: undefined,
  estimatedTaxesPaidOrPlannedJune: undefined,
  estimatedTaxesPaidOrPlannedSeptember: undefined,
  estimatedTaxesPaidOrPlannedJanuary: undefined,
  refundAppliedFromPriorYearFederalReturn: undefined,
  estTaxAtLowerPercent: undefined,
});

export type DivInterestProps = {
  taxableInterest?: number;
  dividends?: number;
  qualifiedDividends?: number;
};
export type DivInterest = RecordOf<DivInterestProps>;
export const mkDivInterest = ImRecord<DivInterestProps>({
  taxableInterest: undefined,
  dividends: undefined,
  qualifiedDividends: undefined,
});

export type CapitalGainsProps = {
  shortTermGains?: number;
  unrecapturedDepreciationGains?: number;
  longTerm28PctGains?: number;
  longTermGains?: number;
  shortTermCarryovers?: number;
  longTermCarryovers?: number;
  altMinDifferent?: boolean;
  shortTermGainsAltMin?: number;
  unrecapturedDepreciationGainsAltMin?: number;
  longTerm28PctGainsAltMin?: number;
};
export type CapitalGains = RecordOf<CapitalGainsProps>;
export const mkCapitalGains = ImRecord<CapitalGainsProps>({
  shortTermGains: undefined,
  unrecapturedDepreciationGains: undefined,
  longTerm28PctGains: undefined,
  longTermGains: undefined,
  shortTermCarryovers: undefined,
  longTermCarryovers: undefined,
  altMinDifferent: undefined,
  shortTermGainsAltMin: undefined,
  unrecapturedDepreciationGainsAltMin: undefined,
  longTerm28PctGainsAltMin: undefined,
});

export type BusinessIncomeProps = {
  revenueSelf?: number;
  costOfGoodsSelf?: number;
  mealsAndEntertainmentSelf?: number;
  otherAllowableExpensesSelf?: number;
  businessUseOfHomeSelf?: number;
  deductibleBusinessMileageSelf?: number;
  unspecifiedBusinessExpensesSelf?: number;
  revenueSpouse?: number;
  costOfGoodsSpouse?: number;
  mealsAndEntertainmentSpouse?: number;
  otherAllowableExpensesSpouse?: number;
  businessUseOfHomeSpouse?: number;
  deductibleBusinessMileageSpouse?: number;
  unspecifiedBusinessExpensesSpouse?: number;
};
export type BusinessIncome = RecordOf<BusinessIncomeProps>;
export const mkBusinessIncome = ImRecord<BusinessIncomeProps>({
  revenueSelf: undefined,
  costOfGoodsSelf: undefined,
  mealsAndEntertainmentSelf: undefined,
  otherAllowableExpensesSelf: undefined,
  businessUseOfHomeSelf: undefined,
  deductibleBusinessMileageSelf: undefined,
  unspecifiedBusinessExpensesSelf: undefined,
  revenueSpouse: undefined,
  costOfGoodsSpouse: undefined,
  mealsAndEntertainmentSpouse: undefined,
  otherAllowableExpensesSpouse: undefined,
  businessUseOfHomeSpouse: undefined,
  deductibleBusinessMileageSpouse: undefined,
  unspecifiedBusinessExpensesSpouse: undefined,
});

export type OtherIncomeProps = {
  taxableRefundOfStateLocalIncomeTax?: number;
  alimonyReceived?: number;
  taxableIRAPensionDistributions?: number;
  unemploymentCompensation?: number;
  taxableSocialSecurityBenefits?: number;
  otherIncomeGainsOrLosses?: number;
  socialSecurityRRAIncome?: number;
};
export type OtherIncome = RecordOf<OtherIncomeProps>;
export const mkOtherIncome = ImRecord<OtherIncomeProps>({
  taxableRefundOfStateLocalIncomeTax: undefined,
  alimonyReceived: undefined,
  taxableIRAPensionDistributions: undefined,
  unemploymentCompensation: undefined,
  taxableSocialSecurityBenefits: undefined,
  otherIncomeGainsOrLosses: undefined,
  socialSecurityRRAIncome: undefined,
});

export type ScheduleEProps = {
  partnershipIncome?: number;
  rents?: number;
  royalties?: number;
  depreciation?: number;
  rentalPropertyMileage?: number;
  expenses?: number;
  unspecifiedRentalPropertyExpense?: number;
};
export type ScheduleE = RecordOf<ScheduleEProps>;
export const mkScheduleE = ImRecord<ScheduleEProps>({
  partnershipIncome: undefined,
  rents: undefined,
  royalties: undefined,
  depreciation: undefined,
  rentalPropertyMileage: undefined,
  expenses: undefined,
  unspecifiedRentalPropertyExpense: undefined,
});

export type ScheduleFProps = {
  sales?: number;
  supplementalPayments?: number;
  othIncome?: number;
  expenses?: number;
};
export type ScheduleF = RecordOf<ScheduleFProps>;
export const mkScheduleF = ImRecord<ScheduleFProps>({
  sales: undefined,
  supplementalPayments: undefined,
  othIncome: undefined,
  expenses: undefined,
});

export type AdjustmentsProps = {
  allowableIRADeduction?: number;
  allowableSEHealthInsuranceDeduction?: number;
  keoghSEPDeduction?: number;
  penaltyOnEarlySavingsWithdrawal?: number;
  alimonyPaid?: number;
  movingExpenses?: number;
  otherAdjustments?: number;
};
export type Adjustments = RecordOf<AdjustmentsProps>;
export const mkAdjustments = ImRecord<AdjustmentsProps>({
  allowableIRADeduction: undefined,
  allowableSEHealthInsuranceDeduction: undefined,
  keoghSEPDeduction: undefined,
  penaltyOnEarlySavingsWithdrawal: undefined,
  alimonyPaid: undefined,
  movingExpenses: undefined,
  otherAdjustments: undefined,
});

export type ScheduleADeductionsProps = {
  medicalAndDental?: number;
  realEstateAndOtherTaxes?: number;
  deductibleInvestmentInterest?: number;
  allowableMortgageAndOtherDeductibleInterest?: number;
  charitableContributions?: number;
  deductibleCasualtyLosses?: number;
  miscDeductions?: number;
  canBeClaimedAsDependent?: boolean;
  bBlindSelf?: boolean;
  bBlindSpouse?: boolean;
  b65OrOlderSelf?: boolean;
  b65OrOlderSpouse?: boolean;
  miscDeductionsNoLimit?: number;
};
export type ScheduleADeductions = RecordOf<ScheduleADeductionsProps>;
export const mkScheduleADeductions = ImRecord<ScheduleADeductionsProps>({
  medicalAndDental: undefined,
  realEstateAndOtherTaxes: undefined,
  deductibleInvestmentInterest: undefined,
  allowableMortgageAndOtherDeductibleInterest: undefined,
  charitableContributions: undefined,
  deductibleCasualtyLosses: undefined,
  miscDeductions: undefined,
  canBeClaimedAsDependent: undefined,
  bBlindSelf: undefined,
  bBlindSpouse: undefined,
  b65OrOlderSelf: undefined,
  b65OrOlderSpouse: undefined,
  miscDeductionsNoLimit: undefined,
});

export type StateAndLocalIncomeTaxProps = {
  withholdingsSelf?: number;
  withholdingsSpouse?: number;
  sNextPayDateSelf?: string;
  sNextPayDateSpouse?: string;
  sPayPeriodSelf?: string;
  sPayPeriodSpouse?: string;
  withholdingPerPayPeriodSelf?: number;
  withholdingPerPayPeriodSpouse?: number;
  calcProjectedTotalWithholdingSelfAndSpouse?: number;
  estimatedTaxesPaidToDatePlusProjected?: number;
  taxesPaidThisYearForLastYearsStateTax?: number;
};
export type StateAndLocalIncomeTax = RecordOf<StateAndLocalIncomeTaxProps>;
export const mkStateAndLocalIncomeTax = ImRecord<StateAndLocalIncomeTaxProps>({
  withholdingsSelf: undefined,
  withholdingsSpouse: undefined,
  sNextPayDateSelf: undefined,
  sNextPayDateSpouse: undefined,
  sPayPeriodSelf: undefined,
  sPayPeriodSpouse: undefined,
  withholdingPerPayPeriodSelf: undefined,
  withholdingPerPayPeriodSpouse: undefined,
  calcProjectedTotalWithholdingSelfAndSpouse: undefined,
  estimatedTaxesPaidToDatePlusProjected: undefined,
  taxesPaidThisYearForLastYearsStateTax: undefined,
});

export type TaxesAndCreditsProps = {
  seOtherIncomeSelf?: number;
  seOtherIncomeSpouse?: number;
  seOtherWagesSelf?: number;
  seOtherWagesSpouse?: number;
  amtOtherAdjustmentsAndTaxPreferenceItems?: number;
  otherTax?: number;
  credits?: number;
  amtMedicalExpAdjustmentsItems?: number;
  bNotSubjectToSETaxSelf?: boolean;
  bNotSubjectToSETaxSpouse?: boolean;
  niiagiAdj?: number;
};
export type TaxesAndCredits = RecordOf<TaxesAndCreditsProps>;
export const mkTaxesAndCredits = ImRecord<TaxesAndCreditsProps>({
  seOtherIncomeSelf: undefined,
  seOtherIncomeSpouse: undefined,
  seOtherWagesSelf: undefined,
  seOtherWagesSpouse: undefined,
  amtOtherAdjustmentsAndTaxPreferenceItems: undefined,
  otherTax: undefined,
  credits: undefined,
  amtMedicalExpAdjustmentsItems: undefined,
  bNotSubjectToSETaxSelf: undefined,
  bNotSubjectToSETaxSpouse: undefined,
  niiagiAdj: undefined,
});

export type WithholdingsAndSummaryProps = {
  withholdingsSelf?: number;
  sNextPayDateSelf?: string;
  sPayPeriodSelf?: string;
  withholdingPerPayPeriodSelf?: number;
  withholdingsSpouse?: number;
  sNextPayDateSpouse?: string;
  sPayPeriodSpouse?: string;
  withholdingPerPayPeriodSpouse?: number;
  otherWithholdingsSelf?: number;
  otherWithholdingsSpouse?: number;
};
export type WithholdingsAndSummary = RecordOf<WithholdingsAndSummaryProps>;
export const mkWithholdingsAndSummary = ImRecord<WithholdingsAndSummaryProps>({
  withholdingsSelf: undefined,
  sNextPayDateSelf: undefined,
  sPayPeriodSelf: undefined,
  withholdingPerPayPeriodSelf: undefined,
  withholdingsSpouse: undefined,
  sNextPayDateSpouse: undefined,
  sPayPeriodSpouse: undefined,
  withholdingPerPayPeriodSpouse: undefined,
  otherWithholdingsSelf: undefined,
  otherWithholdingsSpouse: undefined,
});

export type ProfileProps = {
  stModelId?: string;
  numberOfDependents?: number;
  q1SocDec?: number;
  q2SocDec?: number;
  q3SocDec?: number;
  q4SocDec?: number;
  q1Medicare?: number;
  q2Medicare?: number;
  q3Medicare?: number;
  q4Medicare?: number;
  q1FederalIncomeTax?: number;
  q2FederalIncomeTax?: number;
  q3FederalIncomeTax?: number;
  q4FederalIncomeTax?: number;
  q1EstimatedTax?: number;
  q2EstimatedTax?: number;
  q3EstimatedTax?: number;
  q4EstimatedTax?: number;
  q1PaymentLinkedTxnId?: string;
  q2PaymentLinkedTxnId?: string;
  q3PaymentLinkedTxnId?: string;
  q4PaymentLinkedTxnId?: string;
};
export type Profile = RecordOf<ProfileProps>;
export const mkProfile = ImRecord<ProfileProps>({
  stModelId: undefined,
  numberOfDependents: undefined,
  q1SocDec: undefined,
  q2SocDec: undefined,
  q3SocDec: undefined,
  q4SocDec: undefined,
  q1Medicare: undefined,
  q2Medicare: undefined,
  q3Medicare: undefined,
  q4Medicare: undefined,
  q1FederalIncomeTax: undefined,
  q2FederalIncomeTax: undefined,
  q3FederalIncomeTax: undefined,
  q4FederalIncomeTax: undefined,
  q1EstimatedTax: undefined,
  q2EstimatedTax: undefined,
  q3EstimatedTax: undefined,
  q4EstimatedTax: undefined,
  q1PaymentLinkedTxnId: undefined,
  q2PaymentLinkedTxnId: undefined,
  q3PaymentLinkedTxnId: undefined,
  q4PaymentLinkedTxnId: undefined,
});

export type TaxInputProps = {
  id?: string;
  isDeleted?: boolean;
  clientId?: string;
  createdAt?: string;
  modifiedAt?: string;

  profile?: Profile | ProfileProps;

  // calc params
  year?: number;
  filingStatus?: FilingStatus;
  wages?: Wages | WagesProps;
  taxPayments?: TaxPayments | TaxPaymentsProps;
  divInterest?: DivInterest | DivInterestProps;
  capitalGains?: CapitalGains | CapitalGainsProps;
  businessIncome?: BusinessIncome | BusinessIncomeProps;
  otherIncome?: OtherIncome | OtherIncomeProps;
  scheduleE?: ScheduleE | ScheduleEProps;
  scheduleF?: ScheduleF | ScheduleFProps;
  adjustments?: Adjustments | AdjustmentsProps;
  scheduleADeductions?: ScheduleADeductions | ScheduleADeductionsProps;
  stateAndLocalIncomeTax?: StateAndLocalIncomeTax | StateAndLocalIncomeTaxProps;
  taxesAndCredits?: TaxesAndCredits | TaxesAndCreditsProps;
  withholdingsAndSummary?: WithholdingsAndSummary | WithholdingsAndSummaryProps;
};
export type TaxInput = RecordOf<TaxInputProps>;
export const TaxInputRecord = ImRecord<TaxInputProps>({
  id: undefined,
  isDeleted: undefined,
  clientId: undefined,
  createdAt: undefined,
  modifiedAt: undefined,

  year: undefined,

  filingStatus: undefined,
  wages: undefined,
  taxPayments: undefined,
  divInterest: undefined,
  capitalGains: undefined,
  businessIncome: undefined,
  otherIncome: undefined,
  scheduleE: undefined,
  scheduleF: undefined,
  adjustments: undefined,
  scheduleADeductions: undefined,
  stateAndLocalIncomeTax: undefined,
  taxesAndCredits: undefined,
  withholdingsAndSummary: undefined,
  profile: undefined,
});
export const mkTaxInput = (props?: TaxInputProps): TaxInput =>
  TaxInputRecord({
    ...props,
    ...(props?.wages && { wages: ImRecord.isRecord(props.wages) ? props.wages : mkWages(props.wages) }),
    ...(props?.taxPayments && {
      taxPayments: ImRecord.isRecord(props.taxPayments) ? props.taxPayments : mkTaxPayments(props.taxPayments),
    }),
    ...(props?.divInterest && {
      divInterest: ImRecord.isRecord(props.divInterest) ? props.divInterest : mkDivInterest(props.divInterest),
    }),
    ...(props?.capitalGains && {
      capitalGains: ImRecord.isRecord(props.capitalGains) ? props.capitalGains : mkCapitalGains(props.capitalGains),
    }),
    ...(props?.businessIncome && {
      businessIncome: ImRecord.isRecord(props.businessIncome)
        ? props.businessIncome
        : mkBusinessIncome(props.businessIncome),
    }),
    ...(props?.otherIncome && {
      otherIncome: ImRecord.isRecord(props.otherIncome) ? props.otherIncome : mkOtherIncome(props.otherIncome),
    }),
    ...(props?.scheduleE && {
      scheduleE: ImRecord.isRecord(props.scheduleE) ? props.scheduleE : mkScheduleE(props.scheduleE),
    }),
    ...(props?.scheduleF && {
      scheduleF: ImRecord.isRecord(props.scheduleF) ? props.scheduleF : mkScheduleF(props.scheduleF),
    }),
    ...(props?.adjustments && {
      adjustments: ImRecord.isRecord(props.adjustments) ? props.adjustments : mkAdjustments(props.adjustments),
    }),
    ...(props?.scheduleADeductions && {
      scheduleADeductions: ImRecord.isRecord(props.scheduleADeductions)
        ? props.scheduleADeductions
        : mkScheduleADeductions(props.scheduleADeductions),
    }),
    ...(props?.stateAndLocalIncomeTax && {
      stateAndLocalIncomeTax: ImRecord.isRecord(props.stateAndLocalIncomeTax)
        ? props.stateAndLocalIncomeTax
        : mkStateAndLocalIncomeTax(props.stateAndLocalIncomeTax),
    }),
    ...(props?.taxesAndCredits && {
      taxesAndCredits: ImRecord.isRecord(props.taxesAndCredits)
        ? props.taxesAndCredits
        : mkTaxesAndCredits(props.taxesAndCredits),
    }),
    ...(props?.withholdingsAndSummary && {
      withholdingsAndSummary: ImRecord.isRecord(props.withholdingsAndSummary)
        ? props.withholdingsAndSummary
        : mkWithholdingsAndSummary(props.withholdingsAndSummary),
    }),
    ...(props?.profile && {
      profile: ImRecord.isRecord(props.profile) ? props.profile : mkProfile(props.profile),
    }),
  });

export type F1040CalcProps = {
  wagesAndSalaries?: number;
  interestAndDividendIncome?: number;
  businessIncome?: number;
  capitalGainsAndLosses?: number;
  otherIncome?: number;
  totalIncome?: number;
  adjustmentsToIncome?: number;
  adjustedGrossIncome?: number;
  deductions?: number;
  exemptions?: number;
  taxableIncome?: number;
  incomeTax?: number;
  otherTaxesAndCredit?: number;
  totalTax?: number;
  marginalRateOnSalary?: number;
  marginalRateOnCapitalGains?: number;
  marginalRateOnBusinessIncome?: number;
  averageRate?: number;
  actualAndProjectedWithholding?: number;
  actualAndProjectedEstimatedTaxPayments?: number;
  remainingTaxDue?: number;
};
export type F1040Calc = RecordOf<F1040CalcProps>;
export const mkF1040Calc = ImRecord<F1040CalcProps>({
  wagesAndSalaries: undefined,
  interestAndDividendIncome: undefined,
  businessIncome: undefined,
  capitalGainsAndLosses: undefined,
  otherIncome: undefined,
  totalIncome: undefined,
  adjustmentsToIncome: undefined,
  adjustedGrossIncome: undefined,
  deductions: undefined,
  exemptions: undefined,
  taxableIncome: undefined,
  incomeTax: undefined,
  otherTaxesAndCredit: undefined,
  totalTax: undefined,
  marginalRateOnSalary: undefined,
  marginalRateOnCapitalGains: undefined,
  marginalRateOnBusinessIncome: undefined,
  averageRate: undefined,
  actualAndProjectedWithholding: undefined,
  actualAndProjectedEstimatedTaxPayments: undefined,
  remainingTaxDue: undefined,
});

export type WagesCalcProps = {
  selfWagesTotal?: number;
  spouseWagesTotal?: number;
  wagesTotal?: number;
};
export type WagesCalc = RecordOf<WagesCalcProps>;
export const mkWagesCalc = ImRecord<WagesCalcProps>({
  selfWagesTotal: undefined,
  spouseWagesTotal: undefined,
  wagesTotal: undefined,
});

export type BusinessIncomeCalcProps = {
  grossMarginSelf?: number;
  grossMarginSpouse?: number;
  deductibleMealsAndEntertainmentSelf?: number;
  deductibleMealsAndEntertainmentSpouse?: number;
  totalExpensesSelf?: number;
  totalExpensesSpouse?: number;
  businessIncomeSelf?: number;
  businessIncomeSpouse?: number;
};
export type BusinessIncomeCalc = RecordOf<BusinessIncomeCalcProps>;
export const mkBusinessIncomeCalc = ImRecord<BusinessIncomeCalcProps>({
  grossMarginSelf: undefined,
  grossMarginSpouse: undefined,
  deductibleMealsAndEntertainmentSelf: undefined,
  deductibleMealsAndEntertainmentSpouse: undefined,
  totalExpensesSelf: undefined,
  totalExpensesSpouse: undefined,
  businessIncomeSelf: undefined,
  businessIncomeSpouse: undefined,
});

export type CapitalGainsCalcProps = {
  shortTermGainsAltMin?: number;
  unrecapturedDepreciationGainsAltMin?: number;
  longTerm28PctGainsAltMin?: number;
  longTermGainsAltMin?: number;
  netTaxableGains?: number;
};
export type CapitalGainsCalc = RecordOf<CapitalGainsCalcProps>;
export const mkCapitalGainsCalc = ImRecord<CapitalGainsCalcProps>({
  shortTermGainsAltMin: undefined,
  unrecapturedDepreciationGainsAltMin: undefined,
  longTerm28PctGainsAltMin: undefined,
  longTermGainsAltMin: undefined,
  netTaxableGains: undefined,
});

export type ScheduleECalcProps = {
  totalIncome?: number;
  netIncome?: number;
};
export type ScheduleECalc = RecordOf<ScheduleECalcProps>;
export const mkScheduleECalc = ImRecord<ScheduleECalcProps>({
  totalIncome: undefined,
  netIncome: undefined,
});
export type ScheduleFCalcProps = {
  totalIncome?: number;
  netIncome?: number;
};
export type ScheduleFCalc = RecordOf<ScheduleFCalcProps>;
export const mkScheduleFCalc = ImRecord<ScheduleFCalcProps>({
  totalIncome: undefined,
  netIncome: undefined,
});
export type OtherIncomeCalcProps = {
  scheduleEIncome?: number;
  scheduleFIncome?: number;
  totalOtherIncome?: number;
};
export type OtherIncomeCalc = RecordOf<OtherIncomeCalcProps>;
export const mkOtherIncomeCalc = ImRecord<OtherIncomeCalcProps>({
  scheduleEIncome: undefined,
  scheduleFIncome: undefined,
  totalOtherIncome: undefined,
});
export type AdjustmentsCalcProps = {
  halfOfSelfEmploymentTax?: number;
  totalAdjustments?: number;
};
export type AdjustmentsCalc = RecordOf<AdjustmentsCalcProps>;
export const mkAdjustmentsCalc = ImRecord<AdjustmentsCalcProps>({
  halfOfSelfEmploymentTax: undefined,
  totalAdjustments: undefined,
});
export type StateAndLocalIncomeTaxCalcProps = {
  projectedFutureWithholdingSelf?: number;
  projectedTotalWithholdingSelf?: number;
  projectedFutureWithholdingSpouse?: number;
  projectedTotalWithholdingSpouse?: number;
  projectedTotalWithholdingSelfAndSpouse?: number;
  totalEstTaxPaymentsAndWithholdingFullYear?: number;
};
export type StateAndLocalIncomeTaxCalc = RecordOf<StateAndLocalIncomeTaxCalcProps>;
export const mkStateAndLocalIncomeTaxCalc = ImRecord<StateAndLocalIncomeTaxCalcProps>({
  projectedFutureWithholdingSelf: undefined,
  projectedTotalWithholdingSelf: undefined,
  projectedFutureWithholdingSpouse: undefined,
  projectedTotalWithholdingSpouse: undefined,
  projectedTotalWithholdingSelfAndSpouse: undefined,
  totalEstTaxPaymentsAndWithholdingFullYear: undefined,
});
export type ScheduleADeductionsCalcProps = {
  allowedMedical?: number;
  stateAndLocalTax?: number;
  stateLocalTaxAndOtherLimitations?: number;
  totalItemizedDeductions?: number;
  standardDeduction?: number;
  largerOfItemizedOrStandardDeduction?: number;
};
export type ScheduleADeductionsCalc = RecordOf<ScheduleADeductionsCalcProps>;
export const mkScheduleADeductionsCalc = ImRecord<ScheduleADeductionsCalcProps>({
  allowedMedical: undefined,
  stateAndLocalTax: undefined,
  stateLocalTaxAndOtherLimitations: undefined,
  totalItemizedDeductions: undefined,
  standardDeduction: undefined,
  largerOfItemizedOrStandardDeduction: undefined,
});
export type ExemptionsCalcProps = {
  exemptionsSelfAndSpouse?: number;
  totalExemptions?: number;
  exemptionDeductions?: number;
};
export type ExemptionsCalc = RecordOf<ExemptionsCalcProps>;
export const mkExemptionsCalc = ImRecord<ExemptionsCalcProps>({
  exemptionsSelfAndSpouse: undefined,
  totalExemptions: undefined,
  exemptionDeductions: undefined,
});
export type TaxesAndCreditsCalcProps = {
  seScheduleCAndFIncomeSelf?: number;
  seScheduleCAAndFIncomeSpouse?: number;
  seForm1040WagesSelf?: number;
  seForm1040WagesSpouse?: number;
  seTaxSelf?: number;
  seTaxSpouse?: number;
  seTaxTotal?: number;
  amtExcludedDecutions?: number;
  amtAMTTaxableIncome?: number;
  amtRegularTaxBeforeCredits?: number;
  amtTentativeAMT?: number;
  amtAMT?: number;
  netInvIncomeTax?: number;
  totalOtherTaxesAndUsableCredits?: number;
};
export type TaxesAndCreditsCalc = RecordOf<TaxesAndCreditsCalcProps>;
export const mkTaxesAndCreditsCalc = ImRecord<TaxesAndCreditsCalcProps>({
  seScheduleCAndFIncomeSelf: undefined,
  seScheduleCAAndFIncomeSpouse: undefined,
  seForm1040WagesSelf: undefined,
  seForm1040WagesSpouse: undefined,
  seTaxSelf: undefined,
  seTaxSpouse: undefined,
  seTaxTotal: undefined,
  amtExcludedDecutions: undefined,
  amtAMTTaxableIncome: undefined,
  amtRegularTaxBeforeCredits: undefined,
  amtTentativeAMT: undefined,
  amtAMT: undefined,
  netInvIncomeTax: undefined,
  totalOtherTaxesAndUsableCredits: undefined,
});
export type EstTaxCalcProps = {
  suggestedApril?: number;
  suggestedJune?: number;
  suggestedSeptember?: number;
  suggestedJanuary?: number;
};
export type EstTaxCalc = RecordOf<EstTaxCalcProps>;
export const mkEstTaxCalc = ImRecord<EstTaxCalcProps>({
  suggestedApril: undefined,
  suggestedJune: undefined,
  suggestedSeptember: undefined,
  suggestedJanuary: undefined,
});
export type TaxPaymentsCalcProps = {
  totalEstimatedTaxPaymentsAndRefundsApplied?: number;
  estimatedTaxesPaidOrPlanned?: number;
  estTaxSafeHarborPercentagePaid?: number;
};
export type TaxPaymentsCalc = RecordOf<TaxPaymentsCalcProps>;
export const mkTaxPaymentsCalc = ImRecord<TaxPaymentsCalcProps>({
  totalEstimatedTaxPaymentsAndRefundsApplied: undefined,
  estimatedTaxesPaidOrPlanned: undefined,
  estTaxSafeHarborPercentagePaid: undefined,
});
export type WithholdingsAndSummaryCalcProps = {
  projectedFutureWithholdingsSelf?: number;
  projectedTotalWithholdingsSelf?: number;
  projectedFutureWithholdingsSpouse?: number;
  projectedTotalWithholdingsSpouse?: number;
  totalWithholdingsFullYearAndOther?: number;
  projectedTax?: number;
  withholding?: number;
  estimatedTaxPayments?: number;
  remainingTaxDue?: number;
};
export type WithholdingsAndSummaryCalc = RecordOf<WithholdingsAndSummaryCalcProps>;
export const mkWithholdingsAndSummaryCalc = ImRecord<WithholdingsAndSummaryCalcProps>({
  projectedFutureWithholdingsSelf: undefined,
  projectedTotalWithholdingsSelf: undefined,
  projectedFutureWithholdingsSpouse: undefined,
  projectedTotalWithholdingsSpouse: undefined,
  totalWithholdingsFullYearAndOther: undefined,
  projectedTax: undefined,
  withholding: undefined,
  estimatedTaxPayments: undefined,
  remainingTaxDue: undefined,
});

export type TaxOutputProps = {
  stModelId?: string;
  year?: number;

  f1040Calc?: F1040Calc | F1040CalcProps;
  wagesCalc?: WagesCalc | WagesCalcProps;
  businessIncomeCalc?: BusinessIncomeCalc | BusinessIncomeCalcProps;
  capitalGainsCalc?: CapitalGainsCalc | CapitalGainsCalcProps;
  scheduleECalc?: ScheduleECalc | ScheduleECalcProps;
  scheduleFCalc?: ScheduleFCalc | ScheduleFCalcProps;
  otherIncomeCalc?: OtherIncomeCalc | OtherIncomeCalcProps;
  adjustmentsCalc?: AdjustmentsCalc | AdjustmentsCalcProps;
  stateAndLocalIncomeTaxCalc?: StateAndLocalIncomeTaxCalc | StateAndLocalIncomeTaxCalcProps;
  scheduleADeductionsCalc?: ScheduleADeductionsCalc | ScheduleADeductionsCalcProps;
  exemptionsCalc?: ExemptionsCalc | ExemptionsCalcProps;
  taxesAndCreditsCalc?: TaxesAndCreditsCalc | TaxesAndCreditsCalcProps;
  estTaxCalc?: EstTaxCalc | EstTaxCalcProps;
  taxPaymentsCalc?: TaxPaymentsCalc | TaxPaymentsCalcProps;
  withholdingsAndSummaryCalc?: WithholdingsAndSummaryCalc | WithholdingsAndSummaryCalcProps;
};
export type TaxOutput = RecordOf<TaxOutputProps>;
export const TaxOutputRecord = ImRecord<TaxOutputProps>({
  stModelId: undefined,
  year: undefined,
  f1040Calc: undefined,
  wagesCalc: undefined,
  businessIncomeCalc: undefined,
  capitalGainsCalc: undefined,
  scheduleECalc: undefined,
  scheduleFCalc: undefined,
  otherIncomeCalc: undefined,
  adjustmentsCalc: undefined,
  stateAndLocalIncomeTaxCalc: undefined,
  scheduleADeductionsCalc: undefined,
  exemptionsCalc: undefined,
  taxesAndCreditsCalc: undefined,
  estTaxCalc: undefined,
  taxPaymentsCalc: undefined,
  withholdingsAndSummaryCalc: undefined,
});
export const mkTaxOutput = (props?: TaxOutputProps): TaxOutput =>
  TaxOutputRecord({
    ...props,
    ...(props?.f1040Calc && {
      f1040Calc: ImRecord.isRecord(props.f1040Calc) ? props.f1040Calc : mkF1040Calc(props.f1040Calc),
    }),
    ...(props?.wagesCalc && {
      wagesCalc: ImRecord.isRecord(props.wagesCalc) ? props.wagesCalc : mkWagesCalc(props.wagesCalc),
    }),
    ...(props?.businessIncomeCalc && {
      businessIncomeCalc: ImRecord.isRecord(props.businessIncomeCalc)
        ? props.businessIncomeCalc
        : mkBusinessIncomeCalc(props.businessIncomeCalc),
    }),
    ...(props?.capitalGainsCalc && {
      capitalGainsCalc: ImRecord.isRecord(props.capitalGainsCalc)
        ? props.capitalGainsCalc
        : mkCapitalGainsCalc(props.capitalGainsCalc),
    }),
    ...(props?.scheduleECalc && {
      scheduleECalc: ImRecord.isRecord(props.scheduleECalc)
        ? props.scheduleECalc
        : mkScheduleECalc(props.scheduleECalc),
    }),
    ...(props?.scheduleFCalc && {
      scheduleFCalc: ImRecord.isRecord(props.scheduleFCalc)
        ? props.scheduleFCalc
        : mkScheduleFCalc(props.scheduleFCalc),
    }),
    ...(props?.otherIncomeCalc && {
      otherIncomeCalc: ImRecord.isRecord(props.otherIncomeCalc)
        ? props.otherIncomeCalc
        : mkOtherIncomeCalc(props.otherIncomeCalc),
    }),
    ...(props?.adjustmentsCalc && {
      adjustmentsCalc: ImRecord.isRecord(props.adjustmentsCalc)
        ? props.adjustmentsCalc
        : mkAdjustmentsCalc(props.adjustmentsCalc),
    }),
    ...(props?.stateAndLocalIncomeTaxCalc && {
      stateAndLocalIncomeTaxCalc: ImRecord.isRecord(props.stateAndLocalIncomeTaxCalc)
        ? props.stateAndLocalIncomeTaxCalc
        : mkStateAndLocalIncomeTaxCalc(props.stateAndLocalIncomeTaxCalc),
    }),
    ...(props?.scheduleADeductionsCalc && {
      scheduleADeductionsCalc: ImRecord.isRecord(props.scheduleADeductionsCalc)
        ? props.scheduleADeductionsCalc
        : mkScheduleADeductionsCalc(props.scheduleADeductionsCalc),
    }),
    ...(props?.exemptionsCalc && {
      exemptionsCalc: ImRecord.isRecord(props.exemptionsCalc)
        ? props.exemptionsCalc
        : mkExemptionsCalc(props.exemptionsCalc),
    }),
    ...(props?.taxesAndCreditsCalc && {
      taxesAndCreditsCalc: ImRecord.isRecord(props.taxesAndCreditsCalc)
        ? props.taxesAndCreditsCalc
        : mkTaxesAndCreditsCalc(props.taxesAndCreditsCalc),
    }),
    ...(props?.estTaxCalc && {
      estTaxCalc: ImRecord.isRecord(props.estTaxCalc) ? props.estTaxCalc : mkEstTaxCalc(props.estTaxCalc),
    }),
    ...(props?.taxPaymentsCalc && {
      taxPaymentsCalc: ImRecord.isRecord(props.taxPaymentsCalc)
        ? props.taxPaymentsCalc
        : mkTaxPaymentsCalc(props.taxPaymentsCalc),
    }),
    ...(props?.withholdingsAndSummaryCalc && {
      withholdingsAndSummaryCalc: ImRecord.isRecord(props.withholdingsAndSummaryCalc)
        ? props.withholdingsAndSummaryCalc
        : mkWithholdingsAndSummaryCalc(props.withholdingsAndSummaryCalc),
    }),
  });

export type TaxProps = {
  id?: string;
  isDeleted?: boolean;
  clientId?: string;
  createdAt?: string;
  modifiedAt?: string;
  input?: TaxInput | TaxInputProps;
  output?: TaxOutput | TaxOutputProps;
};
export type Tax = RecordOf<TaxProps>;
export const TaxRecord = ImRecord<TaxProps>({
  id: undefined,
  isDeleted: undefined,
  clientId: undefined,
  createdAt: undefined,
  modifiedAt: undefined,
  input: undefined,
  output: undefined,
});
export const mkTax = (props: TaxProps): Tax =>
  TaxRecord({
    ...props,
    input: ImRecord.isRecord(props.input) ? props.input : mkTaxInput(props.input),
    output: !ImRecord.isRecord(props.output) ? mkTaxOutput(props.output) : props.output,
  });

export type PaymentStatus = 'PAID' | 'PENDING' | 'OVERDUE' | 'DUE_TODAY' | 'SKIPPED' | 'UPCOMING' | '';
export type TaxQuarterProps = {
  transactions: ImList<transactionsTypes.TransactionRecord> | transactionsTypes.TransactionRecord[];
  paymentStatus: PaymentStatus;
  dueDate: string;
  amountEstimated: number;
  amountPaid: number;
  startDate: string;
  endDate: string;
  label: 'Q1' | 'Q2' | 'Q3' | 'Q4';
};
export type TaxQuarter = RecordOf<TaxQuarterProps>;
export const mkTaxQuarter = ImRecord<Partial<TaxQuarterProps>>({
  transactions: ImList(),
  paymentStatus: '',
  dueDate: undefined,
  amountEstimated: undefined,
  amountPaid: undefined,
  startDate: undefined,
  endDate: undefined,
  label: undefined,
});

export type TaxPlanStats = {
  amountPaid: number;
  amountEstimated: number;
  transactionsPaid: transactionsTypes.TransactionRecord[];
  transactionsUpcoming: transactionsTypes.TransactionRecord[];
};

export type TaxesStoreProps = QcsSyncResourceStoreProps<TaxProps> & {
  isProcessing: boolean;
};
declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [TAXES_REDUCER_KEY]: QcsSyncResourceStore<TaxProps, TaxesStoreProps>;
  }
}
